import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  FormLayout,
  Toast,
  Loading,
  Thumbnail,
  DropZone,
  InlineError,
  Text,
  Checkbox,
  Select,
} from "@shopify/polaris";
import axios from "../../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../../../Components/LoadingScreen";

const AddEditProjectMindset = ({ mode }) => {
  const navigate = useNavigate();
  const { id, itemId } = useParams();

  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [fileError, setFileError] = useState("");
  const [active, setActive] = useState(false);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");

  const imageKey = useRef("");
  const [item, setItem] = useState({
    image: "",
    is_active: true,
    category: "",
  });

  const toggleActive = () => setActive((a) => !a);

  const toastMarkup = active ? (
    <Toast content="Changes saved successfully" onDismiss={toggleActive} />
  ) : null;

  // 📸 Handle image drop
  const handleImageDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendImage(accepted[0]);
  }, []);

  // 📤 Upload to backend
  const sendImage = (file) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "project");
    form_data.append("image", file);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setItem({
          ...item,
          image: window.URL.createObjectURL(file),
        });
      })
      .catch(console.error)
      .finally(() => setIsImageUploading(false));
  };

  // 🧠 Load project data if editing
  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/projects/${id}/gallery/${itemId}`)
        .then((res) => {
          const d = res.data.data;
          setItem({
            image: d.url || "",
            is_active: d.is_active ? true : false,
          });
          // console.log("categ=", String(item.category?.id));
          setCategory(String(d.category?.id));
          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, []);

  // 📦 Fetch categories for dropdown
  useEffect(() => {
    axios
      .get(`/project-image-category`)
      .then((res) => {
        const options = res.data.data.data.map((cat) => ({
          label: cat.name,
          value: cat.id.toString(),
        }));
        setCategories(options);
      })
      .catch(console.error);
  }, []);

  const validateFields = () => {
    let hasError = false;
    if (!item.image) {
      setFileError("Please upload an image");
      hasError = true;
    } else {
      setFileError("");
    }
    if (!category) {
      setFileError("Please select a category");
      hasError = true;
    }
    return !hasError;
  };

  function handleSave() {
    if (!validateFields()) return;
    setIsSaving(true);

    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");
    formData.append("is_active", item.is_active ? 1 : 0);
    formData.append("category_id", category);
    if (imageKey.current) formData.append("image", imageKey.current);

    axios
      .post(
        mode === "new"
          ? `/projects/${id}/gallery`
          : `/projects/${id}/gallery/${itemId}`,
        formData
      )
      .then(() => {
        mode === "new"
          ? navigate(`/admin/projects-mindset/${id}/images`)
          : toggleActive();
      })
      .catch(console.error)
      .finally(() => setIsSaving(false));
  }

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Project Image`}
      breadcrumbs={[
        { onAction: () => navigate(`/admin/projects-mindset/${id}/images`) },
      ]}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <Text>Image</Text>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: "10px",
            }}
          >
            <Thumbnail size="large" source={item.image || ""} />
            <div style={{ flex: 1 }}>
              <DropZone
                accept=".png,.jpeg,.jpg,.webp"
                allowMultiple={false}
                onDrop={handleImageDrop}
              >
                <DropZone.FileUpload actionHint="Upload an image" />
              </DropZone>
            </div>
          </div>

          {fileError && <InlineError message={fileError} />}

          <Select
            label="Category"
            options={categories}
            onChange={(v) => setCategory(v)}
            value={category}
            placeholder="Select category"
          />

          <Checkbox
            label="Is active"
            checked={item.is_active}
            onChange={(v) => setItem((prev) => ({ ...prev, is_active: v }))}
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              onAction: () => {
                setIsSaving(true);
                axios
                  .delete(`/projects/${id}/gallery/${itemId}`)
                  .then(() => navigate(`/admin/projects-mindset/${id}/images`))
                  .finally(() => setIsSaving(false));
              },
            },
          ]
        }
      />

      {toastMarkup}
      {isImageUploading && <LoadingScreen />}
    </Page>
  );
};

export default AddEditProjectMindset;
